import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Stack, Button, Table, TableBody,
    TableCell, TableHead, TableRow, Divider,
    CircularProgress, Breadcrumbs, Grid, Chip
} from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { getApiUrl, getUploadUrl } from '../config/api';

const InvoiceDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState<any>(null);
    const [school, setSchool] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const config = { headers: { Authorization: `Bearer ${token}` } };

                const invRes = await axios.get(getApiUrl(`invoices/${id}`), config);
                setInvoice(invRes.data);

                try {
                    const schoolRes = await axios.get(getApiUrl('school'), config);
                    setSchool(schoolRes.data);
                } catch (schoolError) {
                    console.warn('Could not fetch school profile, continuing without it.', schoolError);
                }
            } catch (error) {
                console.error('Fetch invoice detail error:', error);
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchData();
    }, [id]);

    const handleResendEmail = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            if (!token) {
                alert('Authentication token not found. Please log in again.');
                return;
            }

            await axios.post(getApiUrl(`invoices/${id}/email`), {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Invoice email resent successfully!');
        } catch (error) {
            console.error('Error resending email:', error);
            alert('Failed to resend invoice email.');
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>;
    if (!invoice) return <Typography sx={{ p: 4 }}>Invoice not found</Typography>;

    const unpaidAmount = Number(invoice.totalAmount) - (invoice.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0);

    return (
        <Box sx={{
            p: { xs: 2, md: 4 },
            bgcolor: { xs: 'white', md: '#F4F7FE' },
            minHeight: 'calc(100vh - 64px)',
            '@media print': { p: 0, bgcolor: 'white' }
        }}>
            {/* Header / Actions - Hidden on Print */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                mb={4}
                spacing={2}
                className="no-print"
            >
                <Breadcrumbs separator="/" aria-label="breadcrumb">
                    <Typography
                        color="text.secondary"
                        sx={{ fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}
                        onClick={() => navigate('/dashboard/invoices')}
                    >
                        Invoices
                    </Typography>
                    <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 800 }}>{invoice.invoiceNo}</Typography>
                </Breadcrumbs>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleResendEmail}
                        sx={{ color: '#263B7C', borderColor: '#263B7C', fontWeight: 700, borderRadius: '8px' }}
                    >
                        EMAIL
                    </Button>
                    {invoice.status !== 'PAID' && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AccountBalanceWalletIcon />}
                            onClick={() => navigate(`/dashboard/payments?invoiceId=${invoice.id}&studentId=${invoice.studentId}&action=new`)}
                            sx={{ bgcolor: '#F9B43F', color: 'black', fontWeight: 800, borderRadius: '8px', '&:hover': { bgcolor: '#e0a038' } }}
                        >
                            PAY
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<PrintIcon />}
                        onClick={() => window.print()}
                        sx={{ bgcolor: '#263B7C', fontWeight: 700, borderRadius: '8px' }}
                    >
                        PRINT
                    </Button>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        color="inherit"
                        size="small"
                        sx={{ fontWeight: 700 }}
                    >
                        Back
                    </Button>
                </Stack>
            </Stack>

            {/* Invoice Document */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: 0, md: '24px' },
                    p: { xs: 3, sm: 6, md: 8 },
                    maxWidth: 900,
                    mx: 'auto',
                    border: { xs: 'none', md: '1px solid #edf2f7' },
                    boxShadow: { xs: 'none', md: '0 10px 40px rgba(0,0,0,0.02)' },
                    '@media print': {
                        p: 0,
                        boxShadow: 'none',
                        width: '100%',
                        maxWidth: '100%',
                        border: 'none',
                        borderRadius: 0
                    },
                    pb: 8
                }}
                className="print-area"
            >
                {/* Header Section */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'center', sm: 'flex-start' }}
                    mb={6}
                    spacing={4}
                    sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                >
                    <Box>
                        {school?.logo && (
                            <Box
                                component="img"
                                src={getUploadUrl(school.logo)}
                                sx={{ height: 75, mb: 2, objectFit: 'contain' }}
                                alt="School Logo"
                            />
                        )}
                        <Typography variant="h6" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                            {school?.name || 'School Name'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                            {school?.address}
                            {'\n'}{school?.phone} | {school?.email}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'center', sm: 'right' } }}>
                        <Typography variant="h4" fontWeight={900} color="#F1F4F9" sx={{ letterSpacing: 2, mb: -1, display: { xs: 'none', md: 'block' } }}>
                            INVOICE
                        </Typography>
                        <Typography variant="h6" fontWeight={900} color="#263B7C" sx={{ letterSpacing: 1, mt: { md: 1 } }}>
                            INVOICE
                        </Typography>
                        <Stack spacing={1} mt={2}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', fontSize: '0.65rem' }}>Invoice No</Typography>
                                <Typography variant="subtitle1" fontWeight={800} color="#2D3748">{invoice.invoiceNo}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Date Issued</Typography>
                                <Typography fontWeight={700}>{new Date(invoice.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Due Date</Typography>
                                <Typography fontWeight={700} color="#C53030">{new Date(invoice.dueDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>

                <Divider sx={{ mb: 6, borderColor: '#edf2f7' }} />

                {/* Bill To & Status */}
                <Grid container spacing={4} mb={6}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 0.5, fontSize: '0.65rem' }}>Bill To</Typography>
                        <Typography variant="subtitle1" fontWeight={900} color="#2D3748">
                            {invoice.student?.firstName || 'Unknown'} {invoice.student?.lastName || 'Student'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                            Student ID: {invoice.student?.studentId || 'N/A'}
                        </Typography>
                        {invoice.student?.grade && (
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                Grade: {invoice.student.grade} {invoice.student.class ? `- ${invoice.student.class}` : ''}
                            </Typography>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1 }}>Status</Typography>
                        <Chip
                            label={invoice.status.replace('_', ' ')}
                            sx={{
                                fontWeight: 900,
                                borderRadius: '8px',
                                bgcolor: invoice.status === 'PAID' ? '#E6FFFA' : invoice.status === 'PARTIALLY_PAID' ? '#FFFBE6' : '#FFF5F5',
                                color: invoice.status === 'PAID' ? '#38A169' : invoice.status === 'PARTIALLY_PAID' ? '#B7791F' : '#E53E3E',
                                border: '1px solid',
                                borderColor: 'currentColor',
                                fontSize: '0.75rem'
                            }}
                        />
                        {invoice.term && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Academic Term</Typography>
                                <Typography fontWeight={700}>{invoice.term}</Typography>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {/* Items Table */}
                <Box sx={{ overflowX: 'auto', mb: 6, mx: { xs: -3, sm: 0 } }}>
                    <Table sx={{ minWidth: 600 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#263B7C', '@media print': { bgcolor: '#263B7C !important', printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' } }}>
                                <TableCell sx={{ fontWeight: 800, color: 'white', border: 0 }}>DESCRIPTION</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 800, color: 'white', border: 0 }}>AMOUNT</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoice.items?.map((item: any, idx: number) => (
                                <TableRow key={item.id} sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#F9FAFB' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#4A5568' }}>{item.description}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: '#2D3748' }}>
                                        ${Number(item.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                {/* Totals Section */}
                <Grid container justifyContent="flex-end">
                    <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                        <Stack spacing={2}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary" fontWeight={700}>Subtotal</Typography>
                                <Typography fontWeight={700}>${Number(invoice.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography color="text.secondary" fontWeight={700}>Tax (0%)</Typography>
                                <Typography fontWeight={700}>$0.00</Typography>
                            </Stack>
                            <Box sx={{ bgcolor: '#F4F7FE', p: 2, borderRadius: '12px' }}>
                                <Stack direction="row" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle2" fontWeight={900} color="#263B7C">TOTAL AMOUNT</Typography>
                                    <Typography variant="subtitle2" fontWeight={900} color="#263B7C">
                                        ${Number(invoice.totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" mb={1}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700}>Total Paid</Typography>
                                    <Typography variant="caption" fontWeight={700} color="#38A169">
                                        -${(invoice.receipts?.reduce((sum: number, r: any) => sum + Number(r.amount), 0) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </Typography>
                                </Stack>
                                <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="subtitle1" fontWeight={900} color="#C53030">BALANCE DUE</Typography>
                                    <Typography variant="subtitle1" fontWeight={900} color="#C53030">
                                        ${unpaidAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* Footer Section */}
                <Box sx={{ mt: 10, pt: 4, borderTop: '2px dashed #edf2f7', textAlign: 'center' }}>
                    <Typography variant="subtitle2" fontWeight={800} color="#2D3748" mb={1}>
                        Thank you for your prompt payment!
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, maxWidth: 400, display: 'block', mx: 'auto' }}>
                        Please ensure payment is made by the due date to avoid service interruption.
                        Include <strong>{invoice.invoiceNo}</strong> as your payment reference.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 4, opacity: 0.5 }}>
                        Computer generated invoice • {new Date().toLocaleString()} • SquareEdge SSAS
                    </Typography>
                </Box>
            </Paper>

            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body { background: white !important; margin: 0 !important; }
                        .print-area { width: 100% !important; margin: 0 !important; border: none !important; }
                        table { width: 100% !important; }
                        @page { margin: 1.5cm; }
                    }
                `}
            </style>
        </Box>
    );
};

export default InvoiceDetailPage;
